import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

import {
  ApolloError,
  MutationHookOptions,
  MutationTuple,
} from '@apollo/client';

import { alertApiRef, useApi } from '@backstage/core-plugin-api';

import { getError, getTypeOrNull } from './apollo';

// Options that can be passed when using the hook
export type StandardMutationHookOptions<
  TResult,
  TMutation = unknown,
  TVariables = unknown,
> = {
  onComplete?: (result: TResult) => void;
  onError?: 'post-alert' | ((error: Error | ApolloError) => void);
  update?: MutationHookOptions<TMutation, TVariables>['update'];
  optimisticResponse?: MutationHookOptions<
    TMutation,
    TVariables
  >['optimisticResponse'];
};

// The result type for the mutation hook
export type StandardMutationHookResult<
  TName extends string,
  TResult,
  TArgs extends any[] = any[],
> = {
  [K in TName]: (...args: TArgs) => Promise<TResult>;
} & {
  error: Error | ApolloError | null;
  isMutating: boolean;
  called: boolean;
  result?: TResult;
};

// The type for the mutation result
export type MutationResult =
  | {
      __typename?: string;
    }
  | {
      __typename: 'Error';
      message?: string | null;
      code: string;
    }
  | null;

/**
 * Creates a standardized mutation hook with consistent error handling and loading state.
 *
 * @param useMutation - The Apollo mutation hook to use
 * @param mutationField - The field in the mutation result that contains the mutation result
 * @returns A hook that provides mutation functionality with standardized error handling
 *
 * @example
 * ```typescript
 * // Example usage:
 * export const useUpdateStatus = withStandardMutationHook<
 *   UpdateStatusMutation,
 *   StatusFragment,
 *   UpdateStatusMutationVariables,
 *   'updateStatus'
 * >(useUpdateStatusMutation, 'updateStatus');
 * ```
 */
export function withStandardMutationHook<
  TMutation,
  TResult,
  TVariables,
  TField extends string,
  TArgs extends any[] = [TVariables],
>(
  useMutation: (
    options?: MutationHookOptions<TMutation, TVariables>,
  ) => MutationTuple<TMutation, TVariables>,
  mutationField: TField,
  transformInput: (...args: TArgs) => TVariables = ((variables: TVariables) =>
    variables) as any,
  mutationOptions?: Pick<
    MutationHookOptions<TMutation, TVariables>,
    'awaitRefetchQueries' | 'refetchQueries' | 'update' | 'optimisticResponse'
  >,
): (
  options?: StandardMutationHookOptions<TResult, TMutation, TVariables>,
) => StandardMutationHookResult<TField, TResult, TArgs> {
  return (
    options: StandardMutationHookOptions<TResult, TMutation, TVariables> = {},
  ) => {
    const alert = useApi(alertApiRef);
    const [mutate, { loading, error: apolloError, called, data }] =
      useMutation(mutationOptions);

    const result = getTypeOrNull(
      (data as unknown as Record<TField, MutationResult>)?.[mutationField],
    );

    const error = useMemo(() => {
      if (apolloError) {
        return apolloError;
      }

      if (data) {
        const resultError = getError(
          (data as unknown as Record<TField, MutationResult>)[mutationField],
        );

        return resultError;
      }

      return null;
    }, [apolloError, data]);

    // we use a ref to avoid that the onError is called on every render
    // if its not stable.
    const onErrorValue = useRef(options.onError);
    useLayoutEffect(() => {
      onErrorValue.current = options.onError;
    }, [options.onError]);

    useEffect(() => {
      if (error && onErrorValue.current) {
        if (onErrorValue.current === 'post-alert') {
          alert.post({
            severity: 'error',
            message: error.message,
          });
        } else {
          onErrorValue.current(error);
        }
      }
    }, [error, alert, onErrorValue]);

    // we use a ref to avoid that the onComplete is called on every render
    // if its not stable.
    const onCompleteValue = useRef(options.onComplete);
    useLayoutEffect(() => {
      onCompleteValue.current = options.onComplete;
    }, [options.onComplete]);

    useEffect(() => {
      if (result && onCompleteValue.current) {
        onCompleteValue.current(result);
      }
    }, [result]);

    // we use a ref to avoid recreating the mutation callback when update changes
    const updateRef = useRef(options.update);
    useLayoutEffect(() => {
      updateRef.current = options.update;
    }, [options.update]);

    // we use a ref to avoid recreating the mutation callback when optimisticResponse changes
    const optimisticResponseRef = useRef(options.optimisticResponse);
    useLayoutEffect(() => {
      optimisticResponseRef.current = options.optimisticResponse;
    }, [options.optimisticResponse]);

    const mutateItem = useCallback(
      async (...args: TArgs): Promise<TResult> => {
        const variables = transformInput(...args);
        const response = await mutate({
          variables,
          update: updateRef.current,
          optimisticResponse: optimisticResponseRef.current,
        });

        const resultError = getError(
          (response.data as unknown as Record<TField, MutationResult>)[
            mutationField
          ],
        );
        if (resultError) {
          throw new Error(resultError.message || 'Unknown error');
        }

        const callResult: TResult | null = getTypeOrNull(
          (response.data as unknown as Record<TField, MutationResult>)[
            mutationField
          ],
        );
        if (!callResult) {
          throw new Error('Mutation result is null');
        }

        return callResult as TResult;
      },
      [mutate],
    );

    return {
      [mutationField]: mutateItem,
      error,
      isMutating: loading,
      called,
      result,
    } as StandardMutationHookResult<TField, TResult, TArgs>;
  };
}
